// TODO: FB-000000 Remove this from uiLibrary folder. uiLibrary components should not have any business logic.
import React, { useEffect, useState } from 'react';

import classnames from 'classnames';

import HorizontalScroller from 'components/uiLibrary/HorizontalScroller';
import LinkButton from 'components/uiLibrary/LinkButton';
import Loader from 'components/uiLibrary/Loader';
import Popover from 'components/uiLibrary/Popover';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

import { COMPONENTS } from 'components/Globals/Analytics/constants';
import CommonTrans from 'components/Globals/CommonTrans';

import { FILTER_AGGREGATION_SEARCH, PRODUCTIONS } from 'constants/endpointConstants';

import useAppContext from 'utils/hooks/useAppContext';
import useDeviceTypeLayouts from 'utils/hooks/useDeviceTypeLayouts';
import { useQuery } from 'utils/react-query';

import searchQueries from 'containers/Search/queries';

import { useTranslation } from 'src/i18n';

import { AGGREGATION_TYPES, TP, FILTER_TYPES } from 'operabase-router/constants';

import classes from './QuickFilters.module.scss';

const ViewAllDropdown = ({ filterType, aggregationType, filters, getLinkProps, isLink, trackingData = {} }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const [anchorEl, setAnchorEl] = useState(null);

  const { data: items = [], isLoading } = useQuery(
    searchQueries.getDropDownList({
      endpoint: FILTER_AGGREGATION_SEARCH(PRODUCTIONS),
      queryParams: {
        aggregation_type: aggregationType,
        ...(filters && { filters }),
      },
      limit: 300,
      queryConfig: {
        enabled: !!filterType && Boolean(anchorEl),
        select: response =>
          (response?.data?.data || [])?.map(item => ({
            ...item,
            label: item?.name,
          })),
      },
    }),
  );

  return (
    <div>
      <Typography
        variant="h3"
        size="11"
        className={classnames(classes.chipItem, classes.chipDropdown)}
        onClick={e => setAnchorEl(e.currentTarget)}
      >
        {t(`${TP}.ALL_COUNTRIES`)}
        <SpriteIcon icon="keyboard_arrow_down" size={10} className={classes.chipDropdownIcon} />
      </Typography>
      {Boolean(anchorEl) && (
        <Popover
          open
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          classes={{ paper: classes.popoverPaper }}
        >
          {isLoading ? (
            <div className={classes.viewAllDropdownLoader}>
              <Loader small />
            </div>
          ) : (
            items.map((item, index) => (
              <LinkButton
                variant="text"
                key={`${item?.id || ''}-${index}`}
                isLink={isLink}
                styles={{
                  root: classes.dropdownItem,
                }}
                {...getLinkProps(item)}
                disableUnderline
                disableHover
                trackingData={{
                  ...trackingData,
                  component: COMPONENTS.QUICK_FILTERS,
                }}
              >
                <Typography variant="h3" size="11" className={classes.dropdownItemText}>
                  {item.name}
                </Typography>
                <SpriteIcon icon="chevron_right" size={12} className={classes.dropdownItemIcon} />
              </LinkButton>
            ))
          )}
        </Popover>
      )}
    </div>
  );
};

/**
 * QuickFilters component for displaying a horizontally scrollable list of filter options
 * that fetches data from the API based on filterType
 *
 * @param {String} filterType - Type of filter to fetch (maps to aggregation_type)
 * @param {Number} limit - Maximum number of items to fetch (default: 20)
 */
const QuickFilters = ({
  filterType,
  filters,
  limit = 10,
  sticky,
  onCloseClick,
  getLinkProps = () => null,
  sectionTitle,
  isLink = true,
  trackingData = {},
}) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { obRouteContext } = useAppContext();
  const { linkProps } = obRouteContext || {};
  const { filters: routeFilters } = linkProps || {};
  const [selectedItem, setSelectedItem] = useState(routeFilters?.[filterType]?.[0]?.entity || null);
  const { isDesktop } = useDeviceTypeLayouts();

  useEffect(() => {
    setSelectedItem(routeFilters?.[filterType]?.[0]?.entity || null);
  }, [routeFilters, filterType]);

  const aggregationType = AGGREGATION_TYPES[filterType] || filterType;

  const { data: items = [], isLoading, error } = useQuery(
    searchQueries.getDropDownList({
      endpoint: FILTER_AGGREGATION_SEARCH(PRODUCTIONS),
      queryParams: {
        aggregation_type: aggregationType,
        ...(filters && { filters }),
      },
      limit,
      queryConfig: {
        enabled: !!filterType,
        select: response =>
          (response?.data?.data || [])?.map(item => ({
            ...item,
            label: item?.name,
          })),
      },
    }),
  );

  if (isLoading) {
    return (
      <div className={classes.loaderContainer}>
        <Loader size="small" />
      </div>
    );
  }

  if (error || !items || items.length === 0) {
    return null;
  }

  const handleCloseClick = e => {
    e.stopPropagation();
    setSelectedItem(null);

    if (onCloseClick) {
      onCloseClick();
    }
  };

  const FiltersListWrapper = sticky && !isDesktop ? 'div' : HorizontalScroller;

  return (
    <div className={classes.container}>
      <FiltersListWrapper styles={{ root: classes.horizontalScroller }} outset hideArrow>
        <div className={classes.scrollableContent}>
          {filterType === FILTER_TYPES.COUNTRY && (
            <ViewAllDropdown
              filters={filters}
              filterType={filterType}
              aggregationType={aggregationType}
              getLinkProps={getLinkProps}
              isLink={isLink}
              trackingData={trackingData}
            />
          )}
          {items.map((item, index) => {
            const isSelected = selectedItem && selectedItem?.id === item?.id;
            return (
              <div>
                <LinkButton
                  variant="text"
                  key={`${item?.id || ''}-${index}`}
                  isLink={isLink}
                  styles={{
                    root: classnames(classes.chipItem, {
                      [classes.chipItem__selected]: isSelected,
                    }),
                  }}
                  {...getLinkProps(item)}
                  {...(isSelected && {
                    rightIcon: (
                      <SpriteIcon icon="close" size={10} className={classes.closeIcon} ariaLabel="Deselect item" />
                    ),
                    preventDefault: true,
                    onClick: handleCloseClick,
                  })}
                  disableUnderline
                  disableHover
                  trackingData={{
                    ...trackingData,
                    component: COMPONENTS.QUICK_FILTERS,
                  }}
                >
                  <Typography variant="h3" size="11" className={classes.chipText}>
                    <CommonTrans
                      i18nKey={
                        sectionTitle
                          ? t(`${TP}.FN_FILTER_SECTION_BY_OPTION`, { section: sectionTitle, option: item?.name })
                          : item?.name
                      }
                    />
                  </Typography>
                </LinkButton>
              </div>
            );
          })}
        </div>
      </FiltersListWrapper>
    </div>
  );
};

export default QuickFilters;
